import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck,
  Loader2,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC",
];

const FCRA_DISCLOSURE = `By ordering a background check, you confirm: (1) you have a permissible purpose under the FCRA (employment evaluation), (2) the candidate has consented to a background screening, and (3) you will follow pre/post-adverse action steps if you decide not to hire based on the report. Checkr presents FCRA-compliant disclosure and authorization forms in the hosted Apply flow.`;

export default function BackgroundCheckModal({
  open,
  onOpenChange,
  resumeId,
  candidateName,
  candidateEmail,
  onOrdered,
}) {
  const [platformEnabled, setPlatformEnabled] = useState(true);
  const [statusLoading, setStatusLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [workState, setWorkState] = useState("CA");
  const [agree, setAgree] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [ordered, setOrdered] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        if (!cancelled) {
          setOrdered(null);
          setAgree(false);
        }
        const [s, p] = await Promise.all([
          api.get("/background-checks/status"),
          api.get("/background-checks/packages"),
        ]);
        if (cancelled) return;
        setPlatformEnabled(!!s.data.platform_enabled);
        setWorkState(s.data.default_work_state || "CA");
        setPackages(p.data?.packages || []);
        if ((p.data?.packages || []).length > 0) {
          setSelectedSlug(p.data.packages[0].slug);
        }
      } catch {
        if (!cancelled) setPlatformEnabled(false);
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const selectedPkg = useMemo(
    () => packages.find((p) => p.slug === selectedSlug),
    [packages, selectedSlug]
  );

  const order = async () => {
    if (!selectedSlug || !workState) return;
    setOrdering(true);
    try {
      const { data } = await api.post("/background-checks", {
        resume_id: resumeId,
        package_slug: selectedSlug,
        work_state: workState,
      });
      setOrdered(data);
      toast.success("Background check ordered — candidate will receive Checkr invite");
      if (onOrdered) onOrdered(data);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not order background check"
      );
    } finally {
      setOrdering(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="background-check-modal"
        className="max-w-lg border border-slate-200"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            Order background check
          </DialogTitle>
        </DialogHeader>

        {statusLoading ? (
          <div className="flex h-24 items-center justify-center text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : !platformEnabled ? (
          <div
            data-testid="bg-modal-not-configured"
            className="border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          >
            Checkr is not configured on this server yet. Ask your admin to set{" "}
            <code className="font-mono">CHECKR_API_KEY</code> in the backend env.
          </div>
        ) : ordered ? (
          <div data-testid="bg-modal-success" className="space-y-3">
            <div className="flex items-start gap-2 border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              <div>
                Invitation sent. {candidateName || "The candidate"} will receive
                a Checkr email to complete identity verification + FCRA consent.
                You&apos;ll see status updates on this page as Checkr makes
                progress.
              </div>
            </div>
            <div className="space-y-1 text-xs text-slate-600">
              <div>
                <span className="font-bold uppercase tracking-wider">
                  Package:
                </span>{" "}
                {ordered.package_label} (${(ordered.price_cents / 100).toFixed(2)})
              </div>
              <div>
                <span className="font-bold uppercase tracking-wider">
                  Status:
                </span>{" "}
                <span className="capitalize">{(ordered.status || "").replace(/_/g, " ")}</span>
              </div>
              <div>
                <span className="font-bold uppercase tracking-wider">
                  Checkr env:
                </span>{" "}
                <span className="uppercase">{ordered.checkr_env}</span>
              </div>
            </div>
            {ordered.invitation_url && (
              <a
                href={ordered.invitation_url}
                target="_blank"
                rel="noreferrer"
                data-testid="bg-invitation-url"
                className="inline-flex items-center gap-1 text-sm font-bold text-blue-700 hover:underline"
              >
                Preview the candidate invite link{" "}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
            <Button
              onClick={() => onOpenChange(false)}
              variant="outline"
              data-testid="bg-modal-done"
            >
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-xs text-slate-600">
              Ordering a background check for{" "}
              <span className="font-semibold text-slate-800">
                {candidateName || "this candidate"}
              </span>
              {candidateEmail && (
                <span className="text-slate-500"> ({candidateEmail})</span>
              )}
              .
            </div>

            <div>
              <label
                htmlFor="bg-pkg"
                className="text-xs font-bold uppercase tracking-wider text-slate-500"
              >
                Package
              </label>
              <select
                id="bg-pkg"
                data-testid="bg-package-select"
                value={selectedSlug}
                onChange={(e) => setSelectedSlug(e.target.value)}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500"
              >
                {packages.map((p) => (
                  <option key={p.slug} value={p.slug}>
                    {p.label} — ${(p.price_cents / 100).toFixed(2)}
                  </option>
                ))}
              </select>
              {selectedPkg?.description && (
                <p className="mt-1 text-[11px] text-slate-500">
                  {selectedPkg.description}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="bg-state"
                className="text-xs font-bold uppercase tracking-wider text-slate-500"
              >
                Work state
              </label>
              <select
                id="bg-state"
                data-testid="bg-state-select"
                value={workState}
                onChange={(e) => setWorkState(e.target.value)}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500"
              >
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[11px] text-slate-500">
                Required by Checkr for jurisdiction-specific disclosures.
              </p>
            </div>

            <div className="border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-900">
              <div className="flex items-start gap-1.5">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <div>{FCRA_DISCLOSURE}</div>
              </div>
              <label className="mt-2 flex items-center gap-2 text-amber-900">
                <input
                  type="checkbox"
                  data-testid="bg-fcra-agree"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                />
                <span>I acknowledge and have a permissible purpose</span>
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="bg-modal-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={order}
                disabled={ordering || !agree || !selectedSlug || !workState}
                data-testid="bg-modal-order"
                className="bg-emerald-600 text-white hover:bg-emerald-500 disabled:bg-slate-300"
              >
                {ordering ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ShieldCheck className="mr-2 h-4 w-4" />
                )}
                Order check
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
